import { useCallback, useEffect, useRef, useState } from 'react'
import { agentApi } from '@/lib/api/agent'
import { isConnectionError } from '@/src/lib/assistant/delivery'
import type { PageContext } from '@/src/lib/assistant/session'
import {
  DISCONNECT_GRACE_MS,
  claimMediaSession,
  holdScreenAwake,
  nextCallAction,
  reconnectDelay,
  type ChannelState,
  type MediaSessionLike,
  type PeerState,
  type WakeLockLike,
} from '@/src/lib/voice/lifecycle'
import { openingFor, prepareChime, type PreparedChime } from '@/src/lib/voice/opening'
import {
  approvalNote,
  functionCallOutput,
  greeting,
  pageNote,
  readServerEvent,
  responseCreate,
  type VoiceFunctionCall,
  type VoiceStatus,
} from '@/src/lib/voice/events'
import { VOICE_STATUS_LABEL } from './VoicePanel'

export interface VoiceLine {
  id: string
  role: 'user' | 'assistant'
  text: string
}

interface Options {
  /** A thread was made for the call; the page should show it. */
  onThreadCreated: (threadId: string) => void
  /** Something changed in the thread (a change awaits approval, a draft arrived, the call ended). */
  onThreadChanged: () => void
}

function describeStartError(cause: unknown): string {
  if (cause instanceof DOMException && cause.name === 'NotAllowedError') {
    return 'Helm needs your microphone for voice. Allow it in the browser and try again.'
  }
  if (cause instanceof DOMException && cause.name === 'NotFoundError') {
    return 'No microphone was found.'
  }
  if (isConnectionError(cause)) {
    return 'Lost connection to Beacon. Check your connection and start the call again.'
  }
  return cause instanceof Error ? cause.message : 'Voice could not start'
}

const visible = () => typeof document === 'undefined' || document.visibilityState === 'visible'

/**
 * A voice conversation with the assistant over WebRTC.
 *
 * Helm's API opens the call with OpenAI (no key reaches the browser). The model
 * speaks through an audio element; its events arrive on the `oai-events` data
 * channel. Tool calls are sent to Helm's API, which runs them as the signed-in
 * person; a change comes back awaiting approval on screen, as in text.
 *
 * The call survives what it can. Its audio keeps playing with the tab in the
 * background; it sits on the lock screen and headset buttons through Media
 * Session; the screen is kept on while Helm is showing. When the line drops
 * anyway (a locked iPhone stops the microphone), it reconnects on the same
 * conversation once the page is back, without greeting again and keeping
 * mute as it was.
 */
export function useVoiceConversation({ onThreadCreated, onThreadChanged }: Options) {
  const [status, setStatus] = useState<VoiceStatus>('idle')
  const [lines, setLines] = useState<VoiceLine[]>([])
  const [muted, setMutedState] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [threadId, setThreadId] = useState<string | null>(null)

  const peer = useRef<RTCPeerConnection | null>(null)
  const channel = useRef<RTCDataChannel | null>(null)
  const microphone = useRef<MediaStream | null>(null)
  const speaker = useRef<HTMLAudioElement | null>(null)
  const runId = useRef<string | null>(null)
  /** The opening greeting's response: its line is shown, not kept in the thread. */
  const greetingResponse = useRef<string | null>(null)
  const chime = useRef<PreparedChime | null>(null)
  /** The person wants the call up: set from Talk until End. */
  const wanted = useRef(false)
  const callThread = useRef<string | null>(null)
  const callPage = useRef<PageContext | null>(null)
  const mutedRef = useRef(false)
  const attempts = useRef(0)
  const reconnecting = useRef(false)
  const graceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Bumped per connection; a handler from a closed one sees it moved on and does nothing. */
  const generation = useRef(0)
  const callbacks = useRef({ onThreadCreated, onThreadChanged })
  useEffect(() => {
    callbacks.current = { onThreadCreated, onThreadChanged }
  })

  const send = useCallback((event: Record<string, unknown>) => {
    if (channel.current?.readyState === 'open') channel.current.send(JSON.stringify(event))
  }, [])

  const keep = useCallback((role: 'user' | 'assistant', text: string) => {
    if (!runId.current) return
    void agentApi.keepVoiceTranscript({ run_id: runId.current, role, text }).catch(() => undefined)
  }, [])

  const runTools = useCallback(
    async (calls: VoiceFunctionCall[]) => {
      if (!runId.current) return
      let changed = false
      for (const call of calls) {
        try {
          const result = await agentApi.runVoiceTool({
            run_id: runId.current,
            call_id: call.callId,
            name: call.name,
            arguments: call.arguments,
          })
          // Recorded in the thread: a change awaiting approval, or a reply
          // draft whose card should appear.
          if (result.status === 'AWAITING_CONFIRMATION' || result.tool_call_id) changed = true
          send(functionCallOutput(call.callId, result.output))
        } catch (cause) {
          send(
            functionCallOutput(
              call.callId,
              JSON.stringify({
                error: isConnectionError(cause)
                  ? 'The connection to Helm dropped before this finished. Nothing was changed. Tell the person, then try once more.'
                  : cause instanceof Error
                    ? cause.message
                    : 'The tool failed',
              }),
            ),
          )
        }
      }
      if (changed) callbacks.current.onThreadChanged()
      send(responseCreate())
    },
    [send],
  )

  /** Closes this connection; the call's run on the server ends with it. */
  const teardown = useCallback(() => {
    generation.current += 1
    if (graceTimer.current) clearTimeout(graceTimer.current)
    graceTimer.current = null
    chime.current?.discard()
    chime.current = null
    const closing = { channel: channel.current, peer: peer.current, mic: microphone.current }
    channel.current = null
    peer.current = null
    microphone.current = null
    closing.channel?.close()
    closing.peer?.close()
    closing.mic?.getTracks().forEach((track) => track.stop())
    if (speaker.current) speaker.current.srcObject = null
    const ended = runId.current
    runId.current = null
    if (ended) void agentApi.endVoice(ended).catch(() => undefined)
    return ended
  }, [])

  const stop = useCallback(() => {
    wanted.current = false
    reconnecting.current = false
    attempts.current = 0
    if (teardown()) callbacks.current.onThreadChanged()
    setStatus((current) => (current === 'error' ? current : 'idle'))
    mutedRef.current = false
    setMutedState(false)
  }, [teardown])

  // Set below; a connection's handlers call it to report trouble.
  const check = useRef<() => void>(() => undefined)

  const connect = useCallback(
    async (existingThreadId: string | null, page: PageContext | null, reconnect: boolean) => {
      const opening = openingFor({ reconnect })
      // Readied now, inside the click: Safari only starts audio from a gesture.
      chime.current = opening.chime ? prepareChime() : null
      greetingResponse.current = null
      const token = ++generation.current
      const current = () => generation.current === token
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        if (!current()) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }
        microphone.current = stream
        const connection = new RTCPeerConnection()
        peer.current = connection
        speaker.current ??= new Audio()
        speaker.current.autoplay = true
        connection.ontrack = (event) => {
          if (!speaker.current) return
          speaker.current.srcObject = event.streams[0] ?? null
          // Started explicitly too: autoplay alone is not always honoured.
          void speaker.current.play?.()?.catch(() => undefined)
        }
        stream.getAudioTracks().forEach((track) => {
          // Mute survives a reconnect: the person muted, the new line is muted.
          track.enabled = !mutedRef.current
          track.addEventListener?.('ended', () => current() && check.current())
          connection.addTrack(track, stream)
        })
        connection.onconnectionstatechange = () => {
          if (!current()) return
          const state = connection.connectionState
          if (graceTimer.current) clearTimeout(graceTimer.current)
          graceTimer.current = null
          if (state === 'failed') check.current()
          // Often recovers on its own; give it a moment before reconnecting.
          else if (state === 'disconnected') {
            graceTimer.current = setTimeout(() => current() && check.current(), DISCONNECT_GRACE_MS)
          }
        }

        const events = connection.createDataChannel('oai-events')
        channel.current = events
        events.onopen = () => {
          if (!current()) return
          attempts.current = 0
          reconnecting.current = false
          setStatus('listening')
          // A soft chime, then Beacon speaks first: the model's audio takes a
          // moment to arrive, so asking now lands the greeting after the chime.
          chime.current?.play()
          chime.current = null
          if (opening.greet) send(greeting())
        }
        events.onclose = () => current() && check.current()
        events.onmessage = (message) => {
          let event: Record<string, unknown>
          try {
            event = JSON.parse(String(message.data))
          } catch {
            return
          }
          const update = readServerEvent(event)
          if (update.status) setStatus(update.status)
          if (update.error) setError(update.error)
          if (update.greetingResponseId) greetingResponse.current = update.greetingResponseId
          if (update.line) {
            const line = update.line
            setLines((current) =>
              [...current.filter((entry) => entry.id !== line.id), line].slice(-6),
            )
            // The greeting is manners, not conversation: shown, never kept.
            if (!update.responseId || update.responseId !== greetingResponse.current) {
              keep(line.role, line.text)
            }
          }
          if (update.calls) void runTools(update.calls)
        }

        const offer = await connection.createOffer()
        await connection.setLocalDescription(offer)
        const call = await agentApi.startVoice({
          sdp: offer.sdp ?? '',
          thread_id: existingThreadId,
          ...(page ? { page } : {}),
        })
        if (!current()) {
          void agentApi.endVoice(call.run_id).catch(() => undefined)
          return
        }
        runId.current = call.run_id
        callThread.current = call.thread_id
        setThreadId(call.thread_id)
        if (call.thread_id !== existingThreadId) callbacks.current.onThreadCreated(call.thread_id)
        await connection.setRemoteDescription({ type: 'answer', sdp: call.sdp })
      } catch (cause) {
        if (!current()) return
        if (reconnect) {
          // Counted, and tried again or given up on by the next check.
          reconnecting.current = false
          teardown()
          check.current()
          return
        }
        setError(describeStartError(cause))
        setStatus('error')
        stop()
      }
    },
    [keep, runTools, send, stop, teardown],
  )

  const reconnect = useCallback(() => {
    if (reconnecting.current || !wanted.current) return
    reconnecting.current = true
    const attempt = attempts.current
    attempts.current += 1
    setStatus('reconnecting')
    teardown()
    setTimeout(() => {
      if (!wanted.current) return
      void connect(callThread.current, callPage.current, true)
    }, reconnectDelay(attempt))
  }, [connect, teardown])

  // Decides what a call that should be live needs, from what the page can see.
  const checkCall = useCallback(() => {
    if (!wanted.current || reconnecting.current) return
    const track = microphone.current?.getAudioTracks()[0]
    const action = nextCallAction({
      visible: visible(),
      peer: (peer.current?.connectionState as PeerState | undefined) ?? 'closed',
      channel: (channel.current?.readyState as ChannelState | undefined) ?? 'closed',
      microphone: track?.readyState === 'ended' ? 'ended' : 'live',
      attempts: attempts.current,
    })
    if (action === 'reconnect') reconnect()
    else if (action === 'wait') setStatus('reconnecting')
    else if (action === 'give-up') {
      setError('The voice connection dropped and could not be restored. Start it again.')
      setStatus('error')
      stop()
    }
  }, [reconnect, stop])
  useEffect(() => {
    check.current = checkCall
  }, [checkCall])

  const start = useCallback(
    async (existingThreadId: string | null, page?: PageContext | null) => {
      if (wanted.current || peer.current) return
      wanted.current = true
      attempts.current = 0
      callThread.current = existingThreadId
      callPage.current = page ?? null
      setError(null)
      setLines([])
      setStatus('connecting')
      await connect(existingThreadId, page ?? null, false)
    },
    [connect],
  )

  /** Clears a call that ended in an error, once the person has read why. */
  const dismiss = useCallback(() => {
    if (wanted.current) return
    setStatus('idle')
    setError(null)
  }, [])

  const setMuted = useCallback((next: boolean) => {
    mutedRef.current = next
    microphone.current?.getAudioTracks().forEach((track) => {
      track.enabled = !next
    })
    setMutedState(next)
  }, [])

  const toggleMute = useCallback(() => setMuted(!mutedRef.current), [setMuted])

  /** Tells the model what the person decided about a change it put on screen. */
  const reportDecision = useCallback(
    (summary: string, approved: boolean, failure?: string | null) => {
      if (!peer.current) return
      send(approvalNote(summary, approved, failure))
      send(responseCreate())
    },
    [send],
  )

  /** Tells the model the person moved to another page; it answers nothing. */
  const notePage = useCallback(
    (page: PageContext) => {
      callPage.current = page
      if (peer.current) send(pageNote(page))
    },
    [send],
  )

  const active = status !== 'idle' && status !== 'error'

  // While a call is up: back on screen or back online, see whether it needs
  // reconnecting; show it on the lock screen; keep the screen on.
  const media = useRef<ReturnType<typeof claimMediaSession> | null>(null)
  useEffect(() => {
    if (!active) return
    const recheck = () => check.current()
    document.addEventListener('visibilitychange', recheck)
    window.addEventListener('pageshow', recheck)
    window.addEventListener('online', recheck)
    const session = (navigator as Navigator & { mediaSession?: MediaSessionLike }).mediaSession
    media.current = claimMediaSession(
      session,
      { hangUp: stop, setMuted, toggleMute },
      typeof MediaMetadata === 'undefined'
        ? undefined
        : (init) => new MediaMetadata(init as MediaMetadataInit),
    )
    const awake = holdScreenAwake(
      (navigator as Navigator & { wakeLock?: WakeLockLike }).wakeLock,
      document,
    )
    return () => {
      document.removeEventListener('visibilitychange', recheck)
      window.removeEventListener('pageshow', recheck)
      window.removeEventListener('online', recheck)
      media.current?.release()
      media.current = null
      awake.release()
    }
  }, [active, stop, setMuted, toggleMute])
  useEffect(() => {
    media.current?.update({ status: VOICE_STATUS_LABEL[status], muted })
  }, [active, status, muted])

  // Hang up when the page goes away.
  useEffect(() => stop, [stop])

  return {
    status,
    active,
    lines,
    muted,
    error,
    threadId,
    start,
    stop,
    dismiss,
    toggleMute,
    reportDecision,
    notePage,
  }
}
